/**
 * Dynamic Taxonomy Service - Async Protocol Implementation
 *
 * This service implements the correct async API protocol by:
 * 1. Loading JSON files from /docs/taxonomy/ directory
 * 2. Using v1.4.1 layer-specific files (g_layer_v1_4_1.json, s_layer_v1_4_1.json, etc.)
 * 3. No hardcoding or static lookups
 * 4. Dynamic loading based on current taxonomy version
 *
 * This replaces static lookup tables with dynamic JSON loading.
 */

import { logger } from '../utils/logger';
import { TaxonomyItem } from '../types/taxonomy.types';

// Cache for loaded taxonomy data
const taxonomyCache = new Map<string, any>();
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

interface CachedData {
  data: any;
  timestamp: number;
}

/**
 * Get the current taxonomy version from the environment or configuration
 */
function getCurrentTaxonomyVersion(): string {
  // TODO: This should come from backend API or configuration
  // For now, use v1.4.1 as it's the latest available
  return 'v1_4_1';
}

/**
 * Load layer-specific taxonomy data from JSON files
 * @param layer - Layer code (G, S, L, M, W, etc.)
 * @returns Promise with layer taxonomy data
 */
async function loadLayerTaxonomy(layer: string): Promise<any> {
  const version = getCurrentTaxonomyVersion();
  const cacheKey = `${layer}_${version}`;

  // Check cache first
  const cached = taxonomyCache.get(cacheKey) as CachedData;
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    logger.debug(`Using cached taxonomy data for layer ${layer}`);
    return cached.data;
  }

  try {
    const fileName = `${layer.toLowerCase()}_layer_${version}.json`;
    const filePath = `/docs/taxonomy/${fileName}`;

    logger.debug(`Loading taxonomy from: ${filePath}`);

    // Load the JSON file dynamically
    const response = await fetch(filePath);
    if (!response.ok) {
      throw new Error(
        `Failed to load taxonomy file: ${response.status} ${response.statusText}`
      );
    }

    const taxonomyData = await response.json();

    // Cache the loaded data
    taxonomyCache.set(cacheKey, {
      data: taxonomyData,
      timestamp: Date.now(),
    });

    logger.debug(`Successfully loaded taxonomy for layer ${layer}`);
    return taxonomyData;
  } catch (error) {
    logger.error(`Failed to load taxonomy for layer ${layer}:`, error);
    throw error;
  }
}

/**
 * Get all available layers dynamically
 * @returns Promise with array of layer codes
 */
export async function getLayers(): Promise<string[]> {
  try {
    // Load the merged taxonomy file to get available layers
    const version = getCurrentTaxonomyVersion();
    const response = await fetch(
      `/docs/taxonomy/taxonomy_${version}_merged.json`
    );

    if (!response.ok) {
      throw new Error(`Failed to load merged taxonomy: ${response.status}`);
    }

    const mergedData = await response.json();

    // Extract layer codes from the scalability features
    if (mergedData.scalability_features?.category_mappings?.layer_codes) {
      const layers = Object.keys(
        mergedData.scalability_features.category_mappings.layer_codes
      );
      logger.debug(`Dynamically loaded layers:`, layers);
      return layers;
    }

    // Fallback: try to detect available layers by checking individual files
    const knownLayers = ['G', 'S', 'L', 'M', 'W', 'B', 'P', 'T', 'C', 'R'];
    const availableLayers: string[] = [];

    for (const layer of knownLayers) {
      try {
        const fileName = `${layer.toLowerCase()}_layer_${version}.json`;
        const testResponse = await fetch(`/docs/taxonomy/${fileName}`, {
          method: 'HEAD',
        });
        if (testResponse.ok) {
          availableLayers.push(layer);
        }
      } catch {
        // Layer file doesn't exist, skip it
      }
    }

    logger.debug(`Detected available layers:`, availableLayers);
    return availableLayers;
  } catch (error) {
    logger.error('Failed to get layers dynamically:', error);
    // Final fallback to known layers
    return ['G', 'S', 'L', 'M', 'W', 'B', 'P', 'T', 'C', 'R'];
  }
}

/**
 * Get categories for a specific layer dynamically
 * @param layer - Layer code
 * @returns Promise with array of TaxonomyItem
 */
export async function getCategories(layer: string): Promise<TaxonomyItem[]> {
  try {
    const taxonomyData = await loadLayerTaxonomy(layer);

    // Extract categories from the layer data
    const layerData = taxonomyData[layer];
    if (!layerData || !layerData.categories) {
      logger.warn(`No categories found for layer ${layer}`);
      return [];
    }

    const categories: TaxonomyItem[] = [];

    // Convert categories to TaxonomyItem format
    Object.entries(layerData.categories).forEach(
      ([numericCode, categoryData]: [string, any]) => {
        categories.push({
          id: categoryData.id || parseInt(numericCode) || 0,
          code: categoryData.code,
          name: categoryData.name,
          numericCode: numericCode,
        });
      }
    );

    logger.debug(`Loaded ${categories.length} categories for layer ${layer}`);
    return categories;
  } catch (error) {
    logger.error(`Failed to get categories for layer ${layer}:`, error);
    return [];
  }
}

/**
 * Get subcategories for a specific layer and category dynamically
 * @param layer - Layer code
 * @param categoryCode - Category code
 * @returns Promise with array of TaxonomyItem
 */
export async function getSubcategories(
  layer: string,
  categoryCode: string
): Promise<TaxonomyItem[]> {
  try {
    const taxonomyData = await loadLayerTaxonomy(layer);

    // Find the category by code
    const layerData = taxonomyData[layer];
    if (!layerData || !layerData.categories) {
      logger.warn(`No layer data found for ${layer}`);
      return [];
    }

    // Find category by code (not numeric code)
    let categoryEntry: any = null;

    Object.entries(layerData.categories).forEach(
      ([numericCode, categoryData]: [string, any]) => {
        if (categoryData.code === categoryCode) {
          categoryEntry = categoryData;
        }
      }
    );

    if (!categoryEntry || !categoryEntry.subcategories) {
      logger.warn(`No subcategories found for ${layer}.${categoryCode}`);
      return [];
    }

    const subcategories: TaxonomyItem[] = [];

    // Convert subcategories to TaxonomyItem format
    Object.entries(categoryEntry.subcategories).forEach(
      ([numericCode, subcategoryData]: [string, any]) => {
        subcategories.push({
          id: subcategoryData.id || parseInt(numericCode) || 0,
          code: subcategoryData.code,
          name: subcategoryData.name,
          numericCode: numericCode,
        });
      }
    );

    logger.debug(
      `Loaded ${subcategories.length} subcategories for ${layer}.${categoryCode}`
    );
    return subcategories;
  } catch (error) {
    logger.error(
      `Failed to get subcategories for ${layer}.${categoryCode}:`,
      error
    );
    return [];
  }
}

/**
 * Convert HFN to MFA using dynamic taxonomy data
 * @param hfn - Human-Friendly Name
 * @returns Promise with MFA string
 */
export async function convertHFNtoMFA(hfn: string): Promise<string> {
  if (!hfn) return '';

  try {
    const parts = hfn.split('.');
    if (parts.length < 4) {
      logger.warn(`Invalid HFN format: ${hfn}`);
      return '';
    }

    const [layer, categoryCode, subcategoryCode, sequential] = parts;

    // Get layer numeric code
    const layerNumericCodes: Record<string, string> = {
      G: '1',
      S: '2',
      L: '3',
      M: '4',
      W: '5',
      B: '6',
      P: '7',
      T: '8',
      C: '9',
      R: '10',
    };

    const layerMFA = layerNumericCodes[layer.toUpperCase()];
    if (!layerMFA) {
      logger.warn(`Unknown layer: ${layer}`);
      return '';
    }

    // Load taxonomy data for the layer
    const taxonomyData = await loadLayerTaxonomy(layer);
    const layerData = taxonomyData[layer];

    if (!layerData || !layerData.categories) {
      logger.warn(`No taxonomy data for layer ${layer}`);
      return '';
    }

    // Find category numeric code
    let categoryMFA = '001';
    let subcategoryMFA = '001';

    Object.entries(layerData.categories).forEach(
      ([numericCode, categoryData]: [string, any]) => {
        if (categoryData.code === categoryCode) {
          categoryMFA = numericCode.padStart(3, '0');

          // Find subcategory numeric code
          if (categoryData.subcategories) {
            Object.entries(categoryData.subcategories).forEach(
              ([subNumericCode, subcategoryData]: [string, any]) => {
                if (subcategoryData.code === subcategoryCode) {
                  subcategoryMFA = subNumericCode.padStart(3, '0');
                }
              }
            );
          }
        }
      }
    );

    const sequentialFormatted = sequential.padStart(3, '0');
    const mfa = `${layerMFA}.${categoryMFA}.${subcategoryMFA}.${sequentialFormatted}`;

    logger.debug(`Converted HFN to MFA: ${hfn} → ${mfa}`);
    return mfa;
  } catch (error) {
    logger.error(`Failed to convert HFN to MFA: ${hfn}`, error);
    return '';
  }
}

/**
 * Convert MFA to HFN using dynamic taxonomy data
 * @param mfa - Machine-Friendly Address
 * @returns Promise with HFN string
 */
export async function convertMFAtoHFN(mfa: string): Promise<string> {
  if (!mfa) return '';

  try {
    const parts = mfa.split('.');
    if (parts.length < 4) {
      logger.warn(`Invalid MFA format: ${mfa}`);
      return '';
    }

    const [layerNum, categoryNum, subcategoryNum, sequential] = parts;

    // Get layer code from numeric
    const numericToLayer: Record<string, string> = {
      '1': 'G',
      '2': 'S',
      '3': 'L',
      '4': 'M',
      '5': 'W',
      '6': 'B',
      '7': 'P',
      '8': 'T',
      '9': 'C',
      '10': 'R',
    };

    const layer = numericToLayer[layerNum];
    if (!layer) {
      logger.warn(`Unknown layer numeric: ${layerNum}`);
      return '';
    }

    // Load taxonomy data for the layer
    const taxonomyData = await loadLayerTaxonomy(layer);
    const layerData = taxonomyData[layer];

    if (!layerData || !layerData.categories) {
      logger.warn(`No taxonomy data for layer ${layer}`);
      return '';
    }

    // Find category and subcategory codes
    let categoryCode = 'CAT';
    let subcategoryCode = 'SUB';

    const categoryNumFormatted = categoryNum.padStart(3, '0');
    const subcategoryNumFormatted = subcategoryNum.padStart(3, '0');

    Object.entries(layerData.categories).forEach(
      ([numericCode, categoryData]: [string, any]) => {
        if (numericCode === categoryNumFormatted) {
          categoryCode = categoryData.code;

          // Find subcategory code
          if (categoryData.subcategories) {
            Object.entries(categoryData.subcategories).forEach(
              ([subNumericCode, subcategoryData]: [string, any]) => {
                if (subNumericCode === subcategoryNumFormatted) {
                  subcategoryCode = subcategoryData.code;
                }
              }
            );
          }
        }
      }
    );

    const sequentialFormatted = sequential.padStart(3, '0');
    const hfn = `${layer}.${categoryCode}.${subcategoryCode}.${sequentialFormatted}`;

    logger.debug(`Converted MFA to HFN: ${mfa} → ${hfn}`);
    return hfn;
  } catch (error) {
    logger.error(`Failed to convert MFA to HFN: ${mfa}`, error);
    return '';
  }
}

/**
 * Clear taxonomy cache
 */
export function clearTaxonomyCache(): void {
  taxonomyCache.clear();
  logger.info('Dynamic taxonomy cache cleared');
}

// Export as service object for compatibility
export const dynamicTaxonomyService = {
  getLayers,
  getCategories,
  getSubcategories,
  convertHFNtoMFA,
  convertMFAtoHFN,
  clearTaxonomyCache,
};

export default dynamicTaxonomyService;
